/**
 * SCADA capability gate.
 *
 * Asks the backend whether the signed-in user's organisation has a client_*
 * BigQuery dataset granted (organisation_dataset_access). If it does, plant
 * SCADA is available and the Technical workspace mounts the SCADA screens; if
 * not, they are not rendered at all.
 *
 * Absence is a normal state, not an error — most organisations have market data
 * only. `available: false` with a `reason` is the expected shape for them, so
 * this hook never surfaces an error banner for that case.
 *
 * The response also carries the loaded period (from the ingest batch table),
 * which the screens use as their default window. Defaulting to "last 30 days"
 * would land on an empty range, since O&M data arrives a month in arrears.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Module-level cache, keyed by portfolio: the tab strip and every panel ask on
// mount, and the answer changes only when a grant or a portfolio's assets change.
const _cache = new Map();
const _inflight = new Map();

export function clearScadaCapabilityCache() {
  _cache.clear();
  _inflight.clear();
}

/**
 * @param {string} [portfolioId] When given, the answer also requires the
 *   portfolio to contain the SCADA site's asset. Without it, only the
 *   organisation-level grant is evaluated.
 */
export default function useScadaCapability(portfolioId) {
  const key = portfolioId || '__org__';
  const [cap, setCap] = useState(_cache.get(key) ?? null);
  const [loading, setLoading] = useState(!_cache.has(key));

  useEffect(() => {
    if (_cache.has(key)) { setCap(_cache.get(key)); setLoading(false); return undefined; }

    let cancelled = false;
    setLoading(true);
    if (!_inflight.has(key)) {
      const req = axios.get(`${API_BASE_URL}/api/scada/capability`,
                            { params: portfolioId ? { portfolio_id: portfolioId } : {} })
        .then((r) => { _cache.set(key, r.data); return r.data; })
        .catch(() => {
          const fallback = { available: false, reason: 'capability check failed' };
          _cache.set(key, fallback);
          return fallback;
        })
        .finally(() => { _inflight.delete(key); });
      _inflight.set(key, req);
    }
    _inflight.get(key).then((data) => {
      if (cancelled) return;
      setCap(data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [key, portfolioId]);

  return {
    loading,
    // Both gates must pass when a portfolio is in play.
    available: Boolean(cap?.available),
    // Reported separately so the empty state can say which gate is shut.
    datasetAvailable: Boolean(cap?.dataset_available ?? cap?.available),
    inPortfolio: cap?.in_portfolio ?? null,
    dataset: cap?.dataset ?? null,
    sites: cap?.sites ?? [],
    siteAssetIds: cap?.site_asset_ids ?? [],
    matchedSiteAssetIds: cap?.matched_site_asset_ids ?? [],
    siteName: cap?.site_name ?? null,
    siteAssetId: cap?.site_asset_id ?? null,
    periodStart: cap?.period_start ?? null,
    periodEnd: cap?.period_end ?? null,
    nodeCount: cap?.node_count ?? null,
    reason: cap?.reason ?? null,
  };
}
